import filter from 'lodash/filter.js';
import find from 'lodash/find.js';
import map from 'lodash/map.js';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { notEmpty } from '../../../utils/index.js';

interface IOption {
  value: any;
  label: any;
  [k: string]: any;
}

type UseMultipleValueParams<RefValue, Value, Option> = {
  options: RefValue[];
  key: string;
  value: Value[];
  onChange: (value: RefValue[]) => void;
  createOption: (value: RefValue) => Option;
};

type UseMultipleValueResult<RefValue, Value, Option> = [
  Option[],
  Option[],
  (selectedOptions: readonly Option[]) => void,
];

export const useMultipleValue = <
  RefValue extends Value,
  Value extends Record<string, any>,
  Option extends IOption,
>({
  options,
  key,
  value,
  onChange,
  createOption,
}: UseMultipleValueParams<RefValue, Value, Option>): UseMultipleValueResult<
  RefValue,
  Value,
  Option
> => {
  const selectOptions: Option[] = useMemo(
    () => map(options, createOption),
    [createOption, options],
  );

  const selectValue: Option[] = useMemo(
    () =>
      filter(
        map(
          value,
          (valueItem: Record<string, any>) =>
            find(selectOptions, ['value', valueItem[key]]) || null,
        ),
        notEmpty,
      ),
    [key, selectOptions, value],
  );

  const handleChange = useCallback(
    (selectedOptions: readonly Option[]) => {
      const nextValue = filter(
        map(
          selectedOptions,
          (selectedOption) =>
            find<RefValue>(options, [key, selectedOption.value]) || null,
        ),
        notEmpty,
      );

      onChange?.(nextValue);
    },
    [key, onChange, options],
  );

  return [selectOptions, selectValue, handleChange];
};
